import React, { useEffect, useMemo, useState } from "react";

// Custom components
import ProductsUsage from "./product-usage/productsUsage";
import TransactionStats from "./transaction-stats/transactionStats";
import MultipurposeCard from "./multipurpose-card/multipurposeCard";
import Loading from "../../../Shared/loading/loading";
import CheckList from "./check-list/checkList";

// Hooks
import { useTitle } from "../../../Shared/useTitle";
import { useOutletContext } from "react-router-dom";

// Functions
import { callApi } from "../../../api/fetch";
import {
  mapAppsWithKeyCounts,
  findOrgWallet,
  productARN,
  productName,
  unwrapListData,
  unwrapOrgSummary,
  walletBalanceWithGst,
  APPS_LIST_MAX_LIMIT,
  isTestApp,
} from "../../../utils/dashboardMappers";
import { syncStoredOrganisationId } from "../../../utils/authSession";
import Toast from "../../../Shared/toast/toast";
import type { ApplicationOutletContext } from "../applicationContext";

// Constants
import {
  ONBOARDING_STATUS,
  normalizeOnboardingStatus,
} from "../../../constants/onboardingStatuses";
import { ACCENTCOLOR } from "../../../Shared/colors";
import { App } from "../../../constants/app";

// Styles
import Styles from "./dashboard.module.scss";
import DataTable from "../../../Shared/DataTable/DataTable";
import EnhancedButton from "../../../components/(common)/EnhancedButton";

const tableHeader = [
  {
    name: "App Name",
    className: Styles.date,
  },
  {
    name: "Keys",
    className: Styles.id,
  },
  {
    name: "Products",
    className: Styles.product_name,
  },
];

const plan_types = {
  PREPAID: "PREPAID",
  POSTPAID: "POSTPAID",
};

function Dashboard() {
  useTitle("Zoop Customer Platform | Dashboard");

  const { orgProfile } =
    useOutletContext<ApplicationOutletContext>() ?? { orgProfile: null };
  const org_id = localStorage.getItem("organisation_id");
  const [appData, setAppData] = useState<App[]>([]);

  const [totalNumberOfPages] = useState(0);
  const [onboardingStatus, setOnboardingStatus] = useState("");
  const [selectedAppInfo, setSelectedAppInfo] = useState<any>();
  const [productCount, setProductCount] = useState(0);
  const [planType, setPlanType] = useState<string>("");
  const [tranxnVolume, setTranxnVolume] = useState("0");
  const [latestApprovedProd, setLatestApprovedProduct] = useState("");
  const [productOptions, setProductOptions] = useState<any>([]);
  const [countryCode, setCountryCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [overAllLoading, setOverallLoading] = useState(false);
  const [overallStats, setOverallStats] = useState({
    subscribed_products_count: 0,
    wallets: [] as ReturnType<typeof unwrapOrgSummary>["wallets"],
  });

  const primaryWallet = useMemo(
    () => findOrgWallet(overallStats.wallets, "primary", "production"),
    [overallStats]
  );

  const tableHeaderCustom = (
    <>
      <div className="d-flex justify-content-between p-2 bg-white">
        <p className={"mb-0 card_title_semibold_text text-black"}>
          {appData.length} Apps Running
        </p>

        <EnhancedButton
          to="/application/manage-apps"
          id="dashboard-manage-apps"
          title="Manage Apps"
          size="sm"
          variant="outline-dark"
        />
      </div>
    </>
  );

  function toastTimeout(
    showToastValue: boolean,
    toastType: "success" | "error",
    toastMessage: string | string[],
    heading?: string
  ) {
    if (!showToastValue) return;

    // Show toast via react-toastify
    Toast(
      toastType,
      toastMessage,
      heading || (toastType === "success" ? "Success" : "Error")
    );
  }

  async function getAllAppNames(page: string) {
    if (
      normalizeOnboardingStatus(onboardingStatus) ===
      ONBOARDING_STATUS.PLAN_SUBSCRIBED
    ) {
      setOverallLoading(true);
      try {
        const data = await callApi(`/apps?limit=${APPS_LIST_MAX_LIMIT}`, {
          method: "GET",
        });
        const apps = unwrapListData<App>(data, "apps");
        const appsWithKeyCounts = mapAppsWithKeyCounts(apps);
        setAppData(appsWithKeyCounts);
        const testApp = appsWithKeyCounts.find((app) => isTestApp(app));
        setSelectedAppInfo(testApp ?? appsWithKeyCounts[0]);
        setOverallLoading(false);
      } catch (err) {
        setOverallLoading(false);
        toastTimeout(true, "error", (err as Error).message);
      }
    }
  }

  async function getOrgBalance() {
    try {
      if (
        normalizeOnboardingStatus(onboardingStatus) ===
        ONBOARDING_STATUS.PLAN_SUBSCRIBED
      ) {
        const summary = unwrapOrgSummary(
          await callApi(`/organisations/${org_id}/summary`, {
            method: "GET",
          })
        );
        setOverallStats(summary);
        setProductCount(summary.subscribed_products_count);
      }
    } catch (err) {
      toastTimeout(true, "error", "Failed to fetch");
    }
  }

  function applyOrgProfile(profile: NonNullable<ApplicationOutletContext["orgProfile"]>) {
    syncStoredOrganisationId(profile.organisation_id);
    if (profile.country_code) {
      localStorage.setItem("country_code", profile.country_code);
    }
    setPlanType(profile.plan_type);
    setOnboardingStatus(normalizeOnboardingStatus(profile.onboarding_status));
    setCountryCode(profile.country_code);
    setLoading(false);
  }

  async function getAllProductsForOrg() {
    try {
      const res = await callApi(
        "/products?subscription_status=subscribed",
        {
          method: "GET",
        }
      );
      const products = unwrapListData<Record<string, unknown>>(res, "products");
      const options = products
        .map((product) => ({
          label: productName(product),
          value: productARN(product),
          updatedAt: Number(product.updated_at ?? 0),
        }))
        .filter((option) => option.label);

      setProductOptions([{ label: "All Products", value: "" }, ...options]);

      if (options.length > 0) {
        const latest = [...options].sort(
          (a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0)
        )[0];
        setLatestApprovedProduct(latest.label);
      }
    } catch (err) {
      setProductOptions([{ label: "All Products", value: "" }]);
    }
  }

  // Prefer org profile fetched once by the Application shell.
  useEffect(() => {
    if (!org_id) {
      return;
    }

    if (!orgProfile) {
      setLoading(true);
      return;
    }

    applyOrgProfile(orgProfile);
  }, [org_id, orgProfile]);

  // Load dashboard data only after onboarding reaches plan subscribed.
  useEffect(() => {
    if (
      !org_id ||
      normalizeOnboardingStatus(onboardingStatus) !==
        ONBOARDING_STATUS.PLAN_SUBSCRIBED
    ) {
      return;
    }

    getOrgBalance();
    getAllProductsForOrg();
    getAllAppNames("dashboard");
  }, [org_id, onboardingStatus]);

  //get overall volume of transactions
  useEffect(() => {
    if (!org_id) {
      return;
    }
    async function getTransactionVolume() {
      try {
        const res = await callApi(
          `/organisations/${org_id}/analytics/total-transactions`,
          {
            method: "GET",
            init: {},
          }
        );
        if (
          res &&
          typeof res === "object" &&
          "total_count" in res &&
          (res as { statusCode?: number }).statusCode !== 404
        ) {
          setTranxnVolume(
            String((res as { total_count?: number }).total_count ?? "0")
          );
        }
      } catch (error) {
        console.log(error);
      }
    }
    getTransactionVolume();
  }, [org_id]);

  return (
    <div
      style={{ background: "#F3F4F6", height: "100vh" }}
      className={`container-fluid p-0  ${Styles.container}`}
    >
      {loading || overAllLoading ? (
        <div
          style={{ height: "100vh" }}
          className="d-flex align-items-center justify-content-center"
        >
          <Loading loadingColor={ACCENTCOLOR} />
        </div>
      ) : (
        // The user will not be able to see the dashboard unless the Onboarding
        //  has successfully Completed
        <div
          style={{
            background: "#F3F4F6",
          }}
        >
          {normalizeOnboardingStatus(onboardingStatus) ===
          ONBOARDING_STATUS.PLAN_SUBSCRIBED ? (
            <div>
              <div className="d-flex gap-4 py-4 px-4">
                <div
                  className="d-flex flex-column gap-3"
                  style={{ width: "60%", minWidth: 0 }}
                >
                  <ProductsUsage
                    preselectedProduct={"All Products"}
                    setLatestApprovedProduct={setLatestApprovedProduct}
                    preSelectedDatePreference={"Last 30 days"}
                    productOptions={productOptions}
                  />
                  <TransactionStats
                    loading={loading}
                    appDetails={selectedAppInfo}
                    appData={appData}
                  />
                </div>
                <div
                  className="d-flex flex-column ml-1 gap-2"
                  style={{ width: "40%", minWidth: 0 }}
                >
                  <div className="d-flex gap-2">
                    <MultipurposeCard
                      purpose="trans_info"
                      tranxnVolume={tranxnVolume}
                    />
                    <MultipurposeCard
                      purpose="subscription_info"
                      productCount={productCount}
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <MultipurposeCard
                      purpose="avl_balance_info"
                      availableBalance={
                        planType?.toUpperCase() ===
                        plan_types.POSTPAID.toUpperCase()
                          ? "NA"
                          : walletBalanceWithGst(primaryWallet)
                      }
                    />
                    <MultipurposeCard
                      purpose="trial_info"
                      latestApprovedProd={
                        latestApprovedProd ? latestApprovedProd : "NA"
                      }
                    />
                  </div>
                  <div className={`pl-1 ${Styles.scrollableTable}`}>
                    <DataTable
                      headers={[...tableHeader]}
                      loading={loading}
                      rows={appData}
                      setActivePageNumber={() => {}}
                      totalNumberOfPages={totalNumberOfPages}
                      tableHeader={tableHeaderCustom}
                      purpose="forDashboard_mini"
                      bodyClassName={Styles.scrollableTableBody}
                      pagination={false}
                    >
                      {appData.length ? (
                        appData.map((app: any, index: number) => {
                          return (
                            <tr key={`Dashboard_mini_${index}`}>
                              <td className={`px-4 py-2 ${Styles.appNameCell}`}>
                                <p className={`font body_small_text ${Styles.appNameCell}`}>
                                  <span
                                    className={Styles.truncatedText}
                                    title={app.app_name}
                                  >
                                    {app.app_name}
                                  </span>
                                </p>
                              </td>
                              <td className="px-4 py-2">
                                <p className={"mb-0 body_small_text"}>
                                  {typeof app.keys_count === "number"
                                    ? app.keys_count
                                    : Array.isArray(app.keys)
                                      ? app.keys.length
                                      : 0}
                                </p>
                              </td>
                              <td className="px-4 py-2">
                                <p className={"mb-0 body_small_text"}>
                                  {Array.isArray(app.products_access)
                                    ? app.products_access.length
                                    : 0}
                                </p>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <div className="d-flex align-items-center justify-content-center">
                          No data found
                        </div>
                      )}
                    </DataTable>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <CheckList
              onboardingStatus={onboardingStatus}
              countryCode={countryCode}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default Dashboard;
